'use client'

import { Globe, Plus } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { aggregateToolScores, passingCount } from '@/lib/radar/tool-scores'
import { sortActions } from '@/lib/radar/action-priority'
import { buildVerdict } from '@/lib/radar/verdict'
import { gradeFromScore } from '@/lib/radar/scoring'
import { DashboardHero } from '@/components/radar/DashboardHero'
import { SignalDetailBand } from '@/components/radar/SignalProfile'
import { MetricBand, type Metric } from '@/components/radar/MetricBand'
import {
  DEMO_ACTIONS,
  DEMO_DOMAIN,
  DEMO_ELAPSED,
  DEMO_ERROR_COUNT,
  DEMO_TOOLS,
  DEMO_TREND,
  DEMO_WARNING_COUNT,
} from './radar-demo-data'

/**
 * The Radar dashboard, rendered on the homepage from the REAL product
 * components.
 *
 * Rendered on the homepage AND on /platform — one demo, two surfaces.
 *
 * This is deliberately not a replica. It replaced two hand-drawn copies on
 * 2026-07-26: `RadarDashboardPreview` (832 lines, /platform) and
 * `AuditScoreBoard` (140 lines, home). Both drifted the moment the product
 * changed — the /platform one still depicted the pre-overhaul UI — and their
 * demonstration figures disagreed with each other on every shared check.
 *
 * Realigned to the radar-v2 arrangement (PR #215): the dashboard's Overview
 * tab now reads `DashboardHero` (score, signal profile, next best move, trend)
 * then `SignalDetailBand` then `MetricBand`, so this renders the same three in
 * the same order through the same components. `ScoreHero` and the five
 * `CategoryGrid` cards left with that PR — the signal profile is the same
 * re-cut of the same tool scores, and keeping the cards here would have put
 * the marketing surface back on a layout the product no longer has.
 *
 * The stat strip that used to live inside `ScoreHero` is now `MetricBand`,
 * built from the same four tiles `DashboardOverview` builds, and the
 * hand-built action banner is gone: the hero's own "Next best move" slot reads
 * the top action out of `sortActions`, exactly as the dashboard does.
 *
 * `data-surface='radar-dashboard'` is what makes that possible: the platform
 * palette is scoped to that attribute (globals.css), so the product's dark
 * instrument styling applies inside this wrapper without touching a single
 * marketing token outside it.
 *
 * The frame is a marketing crop, not the whole app: no left navigation rail
 * (it is decoration off the product and collapses badly on a phone), and the
 * top bar's only control is the CTA.
 *
 * Of the product's optional interactive props, one is wired and one is not,
 * on the same test: does the control have somewhere real to go?
 * `onReviewAction` IS passed, so the hero keeps the dashboard's CTA beneath
 * the next move, pointed at /platform. `onCategoryClick` is NOT, because it
 * filters a tool table that this crop does not render, and a control that
 * appears to filter nothing is worse than no control.
 */
interface RadarDemoProps {
  /**
   * Renders the header's "Run an audit" link. Defaults to true so the homepage
   * and /platform are unchanged. The paid-ad landing passes false: that link
   * points at /platform and would send paid traffic off the page it exists to
   * convert. The dashboard itself is the proof, so it stays either way.
   */
  showCta?: boolean
}

export default function RadarDemo({ showCta = true }: RadarDemoProps = {}) {
  const router = useRouter()
  const agg = aggregateToolScores(DEMO_TOOLS)
  const score = agg.score
  const grade = score === null ? 'F' : gradeFromScore(score)

  // The verdict the product would write for this audit. Not transcribed copy:
  // if buildVerdict's wording changes, the homepage changes with it.
  const verdict = buildVerdict({
    score,
    tools: DEMO_TOOLS,
    errorCount: DEMO_ERROR_COUNT,
    warningCount: DEMO_WARNING_COUNT,
  })

  const passing = passingCount(DEMO_TOOLS)
  const quickWins = DEMO_ACTIONS.filter(a => a.effort === 'low').length
  // Never `DEMO_ACTIONS[0]`: the dashboard reads the SORTED list, so the demo
  // has to sort too or it can headline a different action than the product.
  const nextAction = sortActions(DEMO_ACTIONS)[0] ?? null

  // The same four tiles DashboardOverview builds, derived from this same
  // audit. Nothing here is a benchmark, an estimate, or a comparison against
  // other domains.
  const metrics: Metric[] = [
    {
      label: 'Tools completed',
      value: `${agg.completedCount}/13`,
      detail:
        agg.withheldTools.length > 0
          ? `${agg.withheldTools.length} withheld ${
              agg.withheldTools.length === 1 ? 'grade' : 'grades'
            }`
          : 'All sources responded',
    },
    {
      label: 'Checks',
      value: agg.scoredCount ? passing : '--',
      unit: agg.scoredCount ? 'passed' : undefined,
      detail: agg.scoredCount
        ? `of ${agg.scoredCount} gradeable ${
            agg.scoredCount === 1 ? 'tool' : 'tools'
          }`
        : 'No gradeable tools yet',
    },
    {
      label: 'Recommendations',
      value: DEMO_ACTIONS.length,
      unit: 'open',
      color: DEMO_ACTIONS.length > 0 ? 'var(--cta)' : undefined,
      detail:
        quickWins > 0
          ? `${quickWins} quick ${quickWins === 1 ? 'win' : 'wins'}`
          : undefined,
    },
    {
      label: 'Last audit',
      value: DEMO_ELAPSED,
      detail: DEMO_DOMAIN,
    },
  ]

  return (
    <div
      data-surface='radar-dashboard'
      className='overflow-hidden border border-hairline bg-surface-app'
    >
      {/* Top bar — the product's chrome, reduced to identity plus the ask. */}
      <div className='flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline px-4 py-3 sm:px-6'>
        <span className='flex min-w-0 items-center gap-2'>
          <Globe
            size={14}
            aria-hidden='true'
            className='shrink-0 text-muted-foreground'
          />
          <span className='truncate text-sm font-semibold text-foreground'>
            {DEMO_DOMAIN}
          </span>
        </span>
        <span className='hidden font-mono text-[11px] text-instrument sm:inline'>
          {agg.completedCount}/13 tools &middot; last audit today
        </span>
        <span className='flex-1' />
        {showCta && (
          <Link
            href='/platform'
            className='inline-flex shrink-0 items-center gap-1.5 bg-[var(--cta-fill)] px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#c3086e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-app'
          >
            <Plus size={12} aria-hidden='true' />
            Run an audit
          </Link>
        )}
      </div>

      <div className='flex flex-col gap-5 p-4 sm:p-6'>
        <DashboardHero
          score={score}
          grade={grade}
          scoredCount={agg.scoredCount}
          withheldCount={agg.withheldTools.length}
          interpretation={{
            headline: verdict.headline,
            description: verdict.description,
          }}
          activeDomain={DEMO_DOMAIN}
          tools={DEMO_TOOLS}
          nextAction={nextAction}
          // The dashboard's own CTA under the next move, kept rather than
          // dropped: on the product it opens the actions tab, here it goes
          // where the top-bar CTA goes. Same component, same placement, so the
          // panel is not a marketing crop of the app at this point.
          onReviewAction={() => router.push('/platform')}
          isRunning={false}
          // Required on a public page: without it the sparkline would fetch an
          // authenticated route and 401 for every anonymous visitor.
          trend={DEMO_TREND}
        />

        {/* Pillar detail, straight under the chart it belongs to — the same
            position it holds on the dashboard. */}
        <SignalDetailBand tools={DEMO_TOOLS} />

        <MetricBand metrics={metrics} />

        {/* Honesty line. The numbers above are a fixed sample, and saying so
            is cheaper than being caught implying they are a live average. */}
        <span className='block text-[11px] text-instrument'>
          Demonstration audit with fixed sample data. Your own report is
          generated from a live scan of your domain.
        </span>
      </div>
    </div>
  )
}
